import { FiltroActivoKey } from '../../../../core/models';
import { RolPlataforma } from '../../../../core/models/novedades.models';
import { Postulacion } from '../../../../core/models/postulaciones.models';
import {
  ESTADOS_FILTRO_MIS_POSTULACIONES,
  esPostulacionEnviada,
  obtenerIdProyectoPostulacion,
  PostulacionesFiltroCriteria,
} from '../../../../core/utils/postulaciones-filter.util';

export type OrdenCampo = 'fecha_inicio' | 'fecha_termino';
export type OrdenDireccion = 'asc' | 'desc';
export type FiltroActivoItem = { key: FiltroActivoKey; label: string };

export const BUSQUEDA_MINIMA_INSTITUCIONAL = 4;

export const FILTROS_DEFAULT: Record<FiltroActivoKey, string> = {
  concursoSeleccionado: 'Todos',
  subdireccionSeleccionada: 'Todas',
  estadoSeleccionado: 'Todos',
  anioSeleccionado: 'Todos',
  idPostulacionBusqueda: 'Todos',
  idProyectoBusqueda: 'Todos',
  folioBusqueda: 'Todos',
  proyectoBusqueda: 'Todos',
  subdireccionBusqueda: 'Todas',
  estadoBusqueda: 'Todos',
  departamentoBusqueda: 'Todos',
  busquedaGeneral: '',
};

export type PerfilPostulacionesFiltrosState = {
  concursoSeleccionado: string;
  subdireccionSeleccionada: string;
  estadoSeleccionado: string;
  anioSeleccionado: string;
  idPostulacionBusqueda: string;
  idProyectoBusqueda: string;
  folioBusqueda: string;
  proyectoBusqueda: string;
  subdireccionBusqueda: string;
  estadoBusqueda: string;
  departamentoBusqueda: string;
  busquedaGeneral: string;
  ordenarPor: OrdenCampo;
  ordenSeleccionado: OrdenDireccion;
};

export type CatalogosPostulaciones = {
  idsPostulacion: string[];
  idsProyectoDisponibles: string[];
  foliosDisponibles: string[];
  proyectosDisponibles: string[];
  departamentosDisponibles: string[];
  concursos: string[];
  subdirecciones: string[];
  estadosPostulacion: string[];
  anios: number[];
};

export function construirFiltrosActivos(state: PerfilPostulacionesFiltrosState): FiltroActivoItem[] {
  const items: FiltroActivoItem[] = [];

  agregarFiltroSiAplica(items, 'concursoSeleccionado', state.concursoSeleccionado, FILTROS_DEFAULT.concursoSeleccionado, 'Concurso');
  agregarFiltroSiAplica(items, 'subdireccionSeleccionada', state.subdireccionSeleccionada, FILTROS_DEFAULT.subdireccionSeleccionada, 'Subdirección');
  agregarFiltroSiAplica(items, 'estadoSeleccionado', state.estadoSeleccionado, FILTROS_DEFAULT.estadoSeleccionado, 'Estado');
  agregarFiltroSiAplica(items, 'anioSeleccionado', state.anioSeleccionado, FILTROS_DEFAULT.anioSeleccionado, 'Año');
  agregarFiltroSiAplica(items, 'idPostulacionBusqueda', state.idPostulacionBusqueda, FILTROS_DEFAULT.idPostulacionBusqueda, 'ID postulación');
  agregarFiltroSiAplica(items, 'idProyectoBusqueda', state.idProyectoBusqueda, FILTROS_DEFAULT.idProyectoBusqueda, 'ID proyecto');
  agregarFiltroSiAplica(items, 'folioBusqueda', state.folioBusqueda, FILTROS_DEFAULT.folioBusqueda, 'Folio');
  agregarFiltroSiAplica(items, 'proyectoBusqueda', state.proyectoBusqueda, FILTROS_DEFAULT.proyectoBusqueda, 'Proyecto');
  agregarFiltroSiAplica(items, 'subdireccionBusqueda', state.subdireccionBusqueda, FILTROS_DEFAULT.subdireccionBusqueda, 'Subdirección');
  agregarFiltroSiAplica(items, 'estadoBusqueda', state.estadoBusqueda, FILTROS_DEFAULT.estadoBusqueda, 'Estado');
  agregarFiltroSiAplica(items, 'departamentoBusqueda', state.departamentoBusqueda, FILTROS_DEFAULT.departamentoBusqueda, 'Departamento');

  if (tieneTexto(state.busquedaGeneral)) {
    items.push({ key: 'busquedaGeneral', label: `Búsqueda: ${state.busquedaGeneral.trim()}` });
  }

  return items;
}

export function construirCatalogosPostulaciones(postulaciones: Postulacion[]): CatalogosPostulaciones {
  return {
    idsPostulacion: crearCatalogoTexto(postulaciones.map((item) => item.idPostulacion)),
    idsProyectoDisponibles: crearCatalogoTexto(postulaciones.map((item) => obtenerIdProyectoPostulacion(item))),
    foliosDisponibles: crearCatalogoTexto(
      postulaciones
        .filter((item) => esPostulacionEnviada(item.estadoPostulacion))
        .map((item) => item.folio)
        .filter((folio) => !!folio && folio !== '-')
    ),
    proyectosDisponibles: crearCatalogoTexto(postulaciones.map((item) => item.nombreProyecto || item.concurso).filter(Boolean)),
    departamentosDisponibles: crearCatalogoTexto(postulaciones.map((item) => item.departamentoFacultad).filter(Boolean)),
    concursos: crearCatalogoTexto(postulaciones.map((item) => item.concurso).filter(Boolean)),
    subdirecciones: crearCatalogoTextoSinOrdenar(postulaciones.map((item) => item.subdireccion)),
    estadosPostulacion: [...ESTADOS_FILTRO_MIS_POSTULACIONES],
    anios: [...new Set(postulaciones.map((item) => item.anio))].sort((a, b) => b - a),
  };
}

export function construirCriteriaFiltros(
  state: PerfilPostulacionesFiltrosState,
  rolActual: RolPlataforma,
  esRolInstitucional: boolean
): PostulacionesFiltroCriteria {
  const busquedaGeneral =
    esRolInstitucional && tieneTexto(state.busquedaGeneral) && state.busquedaGeneral.trim().length < BUSQUEDA_MINIMA_INSTITUCIONAL
      ? ''
      : state.busquedaGeneral;

  return {
    rolActual,
    concursoSeleccionado: state.concursoSeleccionado,
    subdireccionSeleccionada: state.subdireccionSeleccionada,
    estadoSeleccionado: state.estadoSeleccionado,
    anioSeleccionado: state.anioSeleccionado,
    idPostulacionBusqueda: state.idPostulacionBusqueda === FILTROS_DEFAULT.idPostulacionBusqueda ? '' : state.idPostulacionBusqueda,
    idProyectoBusqueda: state.idProyectoBusqueda === FILTROS_DEFAULT.idProyectoBusqueda ? '' : state.idProyectoBusqueda,
    folioBusqueda: state.folioBusqueda === FILTROS_DEFAULT.folioBusqueda ? '' : state.folioBusqueda,
    proyectoBusqueda: state.proyectoBusqueda === FILTROS_DEFAULT.proyectoBusqueda ? '' : state.proyectoBusqueda,
    subdireccionBusqueda: state.subdireccionBusqueda === FILTROS_DEFAULT.subdireccionBusqueda ? '' : state.subdireccionBusqueda,
    estadoBusqueda: state.estadoBusqueda === FILTROS_DEFAULT.estadoBusqueda ? '' : state.estadoBusqueda,
    departamentoBusqueda: state.departamentoBusqueda === FILTROS_DEFAULT.departamentoBusqueda ? '' : state.departamentoBusqueda,
    busquedaGeneral,
    ordenarPor: state.ordenarPor,
    ordenSeleccionado: state.ordenSeleccionado,
  };
}

export function obtenerPostulacionesVisiblesPorRol(
  postulaciones: Postulacion[],
  rol: RolPlataforma,
  esRolRepresentante: boolean
): Postulacion[] {
  const base = postulaciones.filter((item) => item.rolesPermitidos.includes(rol));
  if (!esRolRepresentante) {
    return base;
  }

  return base.filter((item) => item.estadoInvitacion === 'aceptada' || item.estadoInvitacion === 'rechazada' || item.estadoInvitacion === 'observada');
}

export function valorPorDefectoFiltro(key: FiltroActivoKey): string {
  return FILTROS_DEFAULT[key];
}

function agregarFiltroSiAplica(
  items: FiltroActivoItem[],
  key: FiltroActivoKey,
  valor: string,
  valorPorDefecto: string,
  etiqueta: string
): void {
  if (valor === valorPorDefecto) return;
  items.push({ key, label: `${etiqueta}: ${valor}` });
}

function crearCatalogoTexto(items: string[]): string[] {
  return [...new Set(items.filter((item): item is string => !!item))].sort((a, b) => a.localeCompare(b));
}

function crearCatalogoTextoSinOrdenar(items: string[]): string[] {
  return [...new Set(items.filter((item): item is string => !!item))];
}

function tieneTexto(value: string): boolean {
  return value.trim().length > 0;
}
